import { RefObject, useEffect, useState } from 'react'

type UseOnScreenParams<T extends HTMLElement = HTMLElement> = {
  ref: RefObject<T | null>
  optionsProps?: Partial<IntersectionObserverInit>
}

/**
 * @function useOnScreen - хук возвращает true, когда элемент появляется в viewport
 * IntersectionObserver создаётся в useEffect для SSR (нет на сервере)
 */
export const useOnScreen = <T extends HTMLElement = HTMLElement>(params: UseOnScreenParams<T>): boolean => {
  const { ref, optionsProps } = params
  const [isIntersecting, setIntersecting] = useState(false)
  useEffect(() => {
    const canObserve = typeof IntersectionObserver !== 'undefined' && ref.current
    const onIntersect = ([entry]: IntersectionObserverEntry[]) => {
      setIntersecting(entry.isIntersecting)
    }
    const setup = () => {
      const options: IntersectionObserverInit = { threshold: 0.5, ...optionsProps }
      const observer = new IntersectionObserver(onIntersect, options)
      observer.observe(ref.current)
      return () => observer.disconnect()
    }

    return canObserve ? setup() : undefined
  }, [ref.current, optionsProps])

  return isIntersecting
}
